import type { KeyboardEvent } from 'react';
import type { TFunction } from 'i18next';

import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { PlanningEntityType } from 'generated/index';
import type { OrganizationalUnitId } from 'utils/organizationalUnits';

import { supportedPlanningEntityTypes } from 'components/mrp/components/EntitySearch/EntitySearch.consts';
import { getPlanningEntityTypeByFilterCommand } from 'components/mrp/components/EntitySearch/EntitySearch.utils';
import { formatEntityDisplayLabel } from 'components/mrp/mrp.utils';

import type {
  PlanningEntitySelection,
  PlanningEntityTypeFilterOption
} from './PlanningEntityPicker.types';

/** The type chip for plan units. Never sent to the backend search: plan units are searched client-side. */
export const ORGANIZATIONAL_UNIT_TYPE: PlanningEntityType =
  'ORGANIZATIONAL_UNIT';

/**
 * The type chips to show: the caller's, in the caller's order, minus types this picker does not search;
 * or by default Alle, the searched people types in EntitySearch's order, and Planeenheden.
 */
export const getVisibleTypeFilters = (
  typeFilters: readonly PlanningEntityTypeFilterOption[] | undefined,
  entityTypes: PlanningEntityType[],
  searchOrganizationalUnits: boolean
): { showAll: boolean; types: PlanningEntityType[] } => {
  const searchable = [
    ...supportedPlanningEntityTypes.filter((type) =>
      entityTypes.includes(type)
    ),
    ...(searchOrganizationalUnits ? [ORGANIZATIONAL_UNIT_TYPE] : [])
  ];
  if (!typeFilters) return { showAll: true, types: searchable };

  return {
    showAll: typeFilters.includes('ALL'),
    types: typeFilters.filter(
      (filter): filter is PlanningEntityType =>
        filter !== 'ALL' && searchable.includes(filter)
    )
  };
};

/** The types the search covers, for the placeholder: the active chips, or every searched type. */
export const getSearchedTypes = (
  activeFilter: PlanningEntityType[],
  entityTypes: PlanningEntityType[],
  searchOrganizationalUnits: boolean
) =>
  activeFilter.length > 0
    ? activeFilter
    : getVisibleTypeFilters(undefined, entityTypes, searchOrganizationalUnits)
        .types;

/**
 * The chips on at mount, as the active filter (none is Alle): the default when it is shown; otherwise
 * Alle, or the first chip when Alle is not shown.
 */
export const getInitialActiveFilter = (
  defaultTypeFilter: PlanningEntityTypeFilterOption | undefined,
  { showAll, types }: { showAll: boolean; types: PlanningEntityType[] }
): PlanningEntityType[] => {
  if (
    defaultTypeFilter &&
    defaultTypeFilter !== 'ALL' &&
    types.includes(defaultTypeFilter)
  ) {
    return [defaultTypeFilter];
  }
  if (showAll || types.length === 0) return [];
  return [types[0]];
};

/**
 * A plan group's icon is a folder, as in the plan unit field; a plan unit's a building. A plan group's
 * `uniqId` is a string ("5-group"), a plan unit's a number.
 */
export const getOrganizationalUnitIconType = (
  organizationalUnitId: OrganizationalUnitId
): IconType =>
  typeof organizationalUnitId === 'string' ? 'folder' : 'building';

/** What makes two chips the same: the same person, or the same plan unit from the same perspective. */
export const getSelectionKey = (selection: PlanningEntitySelection) =>
  selection.kind === 'ENTITY'
    ? `ENTITY:${selection.planningEntity.planningEntityType}:${selection.planningEntity.entityId}`
    : `ORGANIZATIONAL_UNIT:${selection.organizationalUnitId}:${selection.perspective}`;

/**
 * What a chip calls a selection: a person by number and name ("12 - Jan Jansen"), a plan unit by name and
 * perspective ("Afdeling Noord · patiënten"). One wording for the chip and for anything that names the
 * selection elsewhere, such as a page's filter summary.
 */
export const getSelectionLabel = (
  selection: PlanningEntitySelection,
  t: TFunction
): string =>
  selection.kind === 'ENTITY'
    ? formatEntityDisplayLabel(selection.label, selection.planningEntity.number)
    : `${selection.label} · ${t(
        `planningEntityPicker.perspectives.${selection.perspective}`
      )}`;

/** The selection with one chip added at the end, unless it is already there. */
export const addSelection = (
  value: PlanningEntitySelection[],
  selection: PlanningEntitySelection
) => {
  const key = getSelectionKey(selection);
  return value.some((current) => getSelectionKey(current) === key)
    ? value
    : [...value, selection];
};

export const removeSelection = (
  value: PlanningEntitySelection[],
  selection: PlanningEntitySelection
) => {
  const key = getSelectionKey(selection);
  return value.filter((current) => getSelectionKey(current) !== key);
};

/**
 * The type a typed shortcut ("/p", "/e", …) switches to: the shared EntitySearch commands, plus "/o" for
 * plan units when they are searchable. The shared util stays as it is, so EntitySearch does not get a type
 * its backend cannot search.
 */
export const getPickerTypeByFilterCommand = (
  event: KeyboardEvent<HTMLElement>,
  searchOrganizationalUnits: boolean
) => {
  if (searchOrganizationalUnits && (event.key === 'o' || event.key === 'O')) {
    return ORGANIZATIONAL_UNIT_TYPE;
  }
  return getPlanningEntityTypeByFilterCommand(event);
};

/** The people types to ask the backend for, or none when the active type chips name only plan units. */
export const getPeopleTypes = (
  activeFilter: PlanningEntityType[],
  entityTypes: PlanningEntityType[]
) => {
  const people = entityTypes.filter(
    (type) => type !== ORGANIZATIONAL_UNIT_TYPE
  );
  return activeFilter.length === 0
    ? people
    : people.filter((type) => activeFilter.includes(type));
};

export const isOrganizationalUnitSearchActive = (
  activeFilter: PlanningEntityType[],
  searchOrganizationalUnits: boolean
) =>
  searchOrganizationalUnits &&
  (activeFilter.length === 0 ||
    activeFilter.includes(ORGANIZATIONAL_UNIT_TYPE));

/**
 * Takes a key from the search field for the picker's own use: no caret movement, and none of the
 * Autocomplete's own handling, which skips an event marked `defaultMuiPrevented`.
 */
export const claimKey = (event: KeyboardEvent<HTMLElement>) => {
  event.preventDefault();
  (
    event as KeyboardEvent & { defaultMuiPrevented?: boolean }
  ).defaultMuiPrevented = true;
};
